import { IFieldBaseProps } from "common/components/Field";
import { FormContext } from "common/components/Form/FormContext";
import { Table } from "common/components/Table";
import { Td } from "common/components/Table/Td";
import { Tr } from "common/components/Table/Tr";
import { ToggleControl } from "common/components/Toggle";
import { getPrettyName } from "common/helpers/Pretty";
import { parsePath } from "common/helpers/FormPath";
import { useAppDispatch, useAppSelector, useAppTracking } from "common/hooks/storeHooks";
import { List } from "immutable";
import React from "react";

export interface ToggleListTableProps extends IFieldBaseProps<"toggleListTable"> {
  options: List<string>;
  customActionName?: string;
  onChange?: (value: List<string>) => void;
  size?: "sm" | "md";
}

export const ToggleListTable: React.FC<ToggleListTableProps> = ({
  id,
  disabled,
  name,
  options,
  onChange,
  customActionName,
  size = "md",
}) => {
  // Hooks
  const { rootPath, updateFormReducer } = React.useContext(FormContext);
  const dispatch = useAppDispatch();
  const { trackEvent } = useAppTracking();

  // Selectors
  const path = parsePath(name);
  const selectedValues: List<string> = useAppSelector((state) => state.getIn(rootPath.concat(path)) as any) || [];

  // Methods
  const handleInput = (event: any) => {
    const { checked, name: inputName } = event.target;
    let updatedSelectedValues;
    if (checked) {
      updatedSelectedValues = selectedValues.concat(inputName).sort();
    } else {
      updatedSelectedValues = selectedValues.filter((value) => value !== inputName);
    }
    dispatch(updateFormReducer({ rootPath, name, value: updatedSelectedValues }));
    trackEvent({
      action: `${checked ? "enable" : "disable"} ${customActionName || inputName}`,
      value: inputName,
    });
    if (onChange) onChange(updatedSelectedValues);
  };

  const value = (option: string) => {
    return selectedValues.includes(option);
  };

  return (
    <div id={id}>
      <Table gridColsClass="grid-cols-12">
        {options.map((option, index) => (
          <Tr key={index} height="lg">
            <Td colSpanClasses="col-span-8" content={getPrettyName(option)}></Td>
            <Td colSpanClasses="col-span-4">
              <div className="flex justify-end w-full">
                <ToggleControl
                  size={size}
                  name={option}
                  disabled={disabled}
                  value={value(option)}
                  handleInput={handleInput}
                />
              </div>
            </Td>
          </Tr>
        ))}
      </Table>
    </div>
  );
};
